<template>
  <div class="homepage-light">
    <nav-bar />

    <section class="legal-section">
      <div class="container">
        <span class="section-label">{{ isEn ? 'Legal' : 'Légal' }}</span>
        <h1>{{ isEn ? 'Terms of Use' : "Conditions Générales d'Utilisation" }}</h1>
        <p class="legal-date">
          {{ isEn ? 'Last updated:' : 'Dernière mise à jour :' }} {{ lastUpdatedDate }}
        </p>

        <div class="legal-content">
          <template v-if="!isEn">
            <h2>1. Objet</h2>
            <p>
              Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation
              de la plateforme <strong>Minds' Academy</strong>, accessible à l'adresse
              <a href="https://mindsacademy.io">mindsacademy.io</a>.
              En accédant au site, vous acceptez sans réserve les présentes CGU.
            </p>

            <h2>2. Description du service</h2>
            <p>
              Minds' Academy est une plateforme éducative destinée aux étudiants, aux enseignants
              et à l'administration. Elle propose notamment :
            </p>
            <ul>
              <li>Une bibliothèque de cours en vidéo, classés par année (1A à 5A).</li>
              <li>Des Free Sessions et des ressources pédagogiques (liens utiles, fichiers PDF, examens corrigés).</li>
              <li>Un espace étudiant pour suivre les contenus de son niveau.</li>
              <li>Un espace enseignant pour publier des supports auprès des groupes.</li>
              <li>Un espace d'administration pour gérer les comptes, les groupes et les ressources.</li>
            </ul>

            <h2>3. Accès au service</h2>
            <p>
              L'accès à certaines fonctionnalités nécessite la création d'un compte utilisateur.
              Vous vous engagez à fournir des informations exactes et à maintenir la confidentialité
              de vos identifiants de connexion.
            </p>
            <p>
              Minds' Academy se réserve le droit de suspendre ou supprimer tout compte en cas de
              non-respect des présentes CGU.
            </p>

            <h2>4. Propriété intellectuelle</h2>
            <p>
              L'ensemble des contenus du site (textes, images, logos, code, maquettes, vidéos de cours)
              est la propriété exclusive de Minds' Academy ou de ses partenaires.
              Toute reproduction, distribution ou utilisation non autorisée est strictement interdite.
            </p>
            <p>
              Les contenus pédagogiques déposés par les enseignants ou l'administration restent leur propriété.
              Minds' Academy les utilise uniquement pour les mettre à disposition des utilisateurs autorisés
              (étudiants, groupes, niveau concerné).
            </p>

            <h2>5. Responsabilités</h2>
            <p>
              Minds' Academy met tout en œuvre pour assurer la disponibilité et la fiabilité du service.
              Toutefois, nous ne garantissons pas un fonctionnement ininterrompu
              et ne saurions être tenus responsables des dommages résultant de :
            </p>
            <ul>
              <li>Interruptions temporaires pour maintenance ou mise à jour.</li>
              <li>Indisponibilité due à des causes externes (hébergeur, réseau, YouTube, etc.).</li>
              <li>Utilisation inappropriée du service par l'utilisateur.</li>
            </ul>

            <h2>6. Contenus pédagogiques</h2>
            <p>
              Les cours, Free Sessions et supports sont fournis à titre pédagogique.
              Ils ne remplacent pas un cursus officiel. L'utilisateur reste responsable de l'usage
              qu'il en fait dans le cadre de ses études.
            </p>

            <h2>7. Protection des données</h2>
            <p>
              Le traitement des données personnelles est détaillé dans notre
              <router-link to="/politique-confidentialite">
                Politique de confidentialité
              </router-link>.
            </p>

            <h2>8. Modification des CGU</h2>
            <p>
              Minds' Academy se réserve le droit de modifier les présentes CGU à tout moment.
              Les utilisateurs seront informés des modifications substantielles.
              La poursuite de l'utilisation du service après modification vaut acceptation
              des nouvelles conditions.
            </p>

            <h2>9. Droit applicable</h2>
            <p>
              Les présentes CGU sont soumises au droit tunisien.
              En cas de litige, les tribunaux tunisiens compétents seront saisis.
            </p>

            <h2>10. Contact</h2>
            <p>
              Pour toute question concernant les présentes CGU, contactez-nous à
              <a href="mailto:contact@mindsacademy.io">contact@mindsacademy.io</a>
              ou via notre <router-link to="/contact">
                formulaire de contact
              </router-link>.
            </p>
          </template>

          <template v-else>
            <h2>1. Purpose</h2>
            <p>
              These Terms of Use govern access to and use of the <strong>Minds' Academy</strong> platform, available at
              <a href="https://mindsacademy.io">mindsacademy.io</a>.
              By accessing the website, you accept these Terms without reservation.
            </p>

            <h2>2. Service description</h2>
            <p>
              Minds' Academy is an education platform for students, teachers and administration. It provides:
            </p>
            <ul>
              <li>A library of video courses, organised by year (1A to 5A).</li>
              <li>Free Sessions and learning resources (useful links, PDF files, corrected exams).</li>
              <li>A student space to follow content for their level.</li>
              <li>A teacher space to publish materials for groups.</li>
              <li>An administration space to manage accounts, groups and resources.</li>
            </ul>

            <h2>3. Access to the service</h2>
            <p>
              Access to certain features requires creating a user account.
              You agree to provide accurate information and to keep your login credentials confidential.
            </p>
            <p>
              Minds' Academy reserves the right to suspend or delete any account in case of non-compliance with these Terms.
            </p>

            <h2>4. Intellectual property</h2>
            <p>
              All content on the website (texts, images, logos, code, mockups, course videos) is the exclusive property of
              Minds' Academy or its partners. Any unauthorized reproduction, distribution or use is strictly prohibited.
            </p>
            <p>
              Educational content uploaded by teachers or administrators remains their property. Minds' Academy uses it only
              to make it available to authorised users (students, groups, relevant level).
            </p>

            <h2>5. Liability</h2>
            <p>
              Minds' Academy makes every effort to ensure service availability and reliability. However, we do not guarantee uninterrupted operation
              and cannot be held liable for damages resulting from:
            </p>
            <ul>
              <li>Temporary interruptions for maintenance or updates.</li>
              <li>Unavailability due to external causes (hosting provider, network, YouTube, etc.).</li>
              <li>Improper use of the service by the user.</li>
            </ul>

            <h2>6. Educational content</h2>
            <p>
              Courses, Free Sessions and materials are provided for educational purposes.
              They do not replace an official curriculum. Users remain responsible for how they use this content in their studies.
            </p>

            <h2>7. Data protection</h2>
            <p>
              Personal data processing is described in our
              <router-link to="/politique-confidentialite">
                privacy policy
              </router-link>.
            </p>

            <h2>8. Changes to these Terms</h2>
            <p>
              Minds' Academy reserves the right to modify these Terms at any time. Users will be informed of substantial changes.
              Continued use of the service after modifications constitutes acceptance of the updated Terms.
            </p>

            <h2>9. Governing law</h2>
            <p>
              These Terms are governed by Tunisian law. In case of dispute, the competent Tunisian courts shall have jurisdiction.
            </p>

            <h2>10. Contact</h2>
            <p>
              For any questions regarding these Terms, contact us at
              <a href="mailto:contact@mindsacademy.io">contact@mindsacademy.io</a>
              or via our <router-link to="/contact">
                contact form
              </router-link>.
            </p>
          </template>
        </div>
      </div>
    </section>

    <page-footer />
  </div>
</template>

<script>
import navBar from '@/views/home/HomePage2/components/navBar.vue'
import pageFooter from '@/views/home/HomePage2/components/pageFooter.vue'

export default {
  name: 'TermsOfUse',
  components: { navBar, pageFooter },
  computed: {
    isEn() {
      return this.$i18n.locale === 'en'
    },
    lastUpdatedDate() {
      const d = new Date('2026-09-20T00:00:00Z')
      const loc = this.isEn ? 'en-US' : 'fr-FR'
      return d.toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' })
    },
    documentTitle() {
      return this.isEn ? "Terms of Use | Minds' Academy" : "CGU | Minds' Academy"
    },
  },
  watch: {
    '$i18n.locale': function () {
      document.title = this.documentTitle
    },
  },
  mounted() {
    document.title = this.documentTitle
    window.scrollTo(0, 0)
  },
  beforeDestroy() {
    document.title = "Minds' Academy"
  },
}
</script>

<style scoped>
.legal-section {
  padding: 120px 0 80px;
  background:
    radial-gradient(700px 340px at 20% 20%, rgba(0,163,255,0.06) 0%, rgba(255,255,255,0) 65%),
    linear-gradient(180deg, #ffffff 0%, var(--bg-sky) 50%, #ffffff 100%);
}

.container {
  max-width: 800px;
  margin: 0 auto;
  padding: 0 24px;
}

.section-label {
  display: inline-block;
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--brand-blue-deep);
  background: rgba(0, 163, 255, 0.10);
  border: 1px solid rgba(0, 163, 255, 0.18);
  padding: 6px 16px;
  border-radius: 50px;
  margin-bottom: 16px;
}

h1 {
  font-size: clamp(28px, 4vw, 38px);
  font-weight: 800;
  color: var(--ink);
  line-height: 1.15;
  margin: 0 0 8px;
  letter-spacing: -0.03em;
}

.legal-date {
  font-size: 14px;
  color: var(--muted);
  margin-bottom: 40px;
}

.legal-content {
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.07);
}

.legal-content h2 {
  font-size: 20px;
  font-weight: 700;
  color: var(--ink);
  margin: 32px 0 12px;
}

.legal-content h2:first-child {
  margin-top: 0;
}

.legal-content p {
  font-size: 15px;
  color: var(--muted);
  line-height: 1.75;
  margin: 0 0 12px;
}

.legal-content ul {
  margin: 0 0 16px;
  padding-left: 24px;
}

.legal-content li {
  font-size: 15px;
  color: var(--muted);
  line-height: 1.75;
  margin-bottom: 6px;
}

.legal-content a {
  color: var(--brand-blue);
  text-decoration: none;
}

.legal-content a:hover {
  text-decoration: underline;
}

@media (max-width: 600px) {
  .legal-content { padding: 24px; }
  h1 { font-size: 26px; }
}
</style>
